import React, { useContext, useEffect, useState } from "react";
import { PageHeader } from "@ant-design/pro-layout";
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Select,
  Popconfirm,
  Popover,
  Input,
  Space,
} from "antd";
import {
  HomeOutlined,
  EditOutlined,
  DeleteOutlined,
  ProfileOutlined,
  EyeOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import {
  getStockAdjustments,
  createStockAdjustments,
  getProductStockByBranch,
} from "../../../network/network";
import TextArea from "antd/lib/input/TextArea";
import { AppContext } from "../../../context/AppContextProvider";
import { getBranches } from "../../../network/network";
import moment from "moment";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { UserType } from "../../../configs/constants";
import { useNavigate } from "react-router-dom";

import QuantityStepper from "../../../components/QuantityStepper";
import { handleApiError, showAppError } from "../../../utils/errorHandler";
import {
  clampQuantityToAvailableStock,
  getAvailableStock,
  isQuantityWithinAvailableStock,
  normalizePositiveQuantity,
} from "../../../utils/inventoryQuantity";
const StockAdjustment = () => {
  const { authUser, appConfig } = useContext(AppContext);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [stockAdjustmentData, setStockAdjustmentData] = useState([]);
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [branchesData, setBranchesData] = useState([]);
  const [branchId, setBranchId] = useState(0);
  const [productsList, setProductsList] = useState([]);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [loadingBTN, setLoadingBTN] = useState(false);
  const { t } = useTranslation();
  const [adjustmentForm] = Form.useForm();
  const [paginationData, setPaginationData] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPageSize, setPerPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState([]);
  let navigate = useNavigate();
  const [searchFormRef] = Form.useForm();

  useEffect(() => {
    setIsDataLoading(true);
    setStockAdjustmentData([]);
    getStockAdjustments({
      ...searchParams,
      page: currentPage,
      per_page: perPageSize,
    })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setStockAdjustmentData(
          response.data.data.stock_adjustments.stock_adjustments,
        );
        setPaginationData(response.data.data.stock_adjustments.pagination);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, currentPage, perPageSize, searchParams, t]);

  useEffect(() => {
    setIsDataLoading(true);
    getBranches({ per_page: -1 })
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchesData(response.data.data.branches);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  useEffect(() => {
    branchId &&
      getProductStockByBranch(branchId)
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          setProductsList(response.data.data.products);
        })
        .catch((error) => handleApiError(error, t));
  }, [branchId, t]);

  const handleSearch = (values) => {
    let search_params = false;
    if (values.filter_by_branch) {
      search_params = {
        filter_by_branch: values.filter_by_branch,
      };
    }

    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };

  const handleReset = () => {
    searchFormRef.resetFields();
    setSearchParams([]);
  };

  const searchForm = (
    <>
      <Form
        name="search-form"
        layout="inline"
        onFinish={handleSearch}
        form={searchFormRef}
      >
        <Form.Item name="filter_by_branch" label={t("branch")}>
          <Select
            className="app-w-200px"
            options={branchesData.map((branch) => {
              return {
                label: branch.name,
                value: branch.name,
              };
            })}
          ></Select>
        </Form.Item>
        <Form.Item>
          <Input
            type="submit"
            value={t("search")}
            className="app-search-submit"
          />
        </Form.Item>
        <Form.Item>
          <Input type="button" value={t("reset")} onClick={handleReset} />
        </Form.Item>
      </Form>
    </>
  );

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "/inventory/stock",
      breadcrumbName: t("inventory"),
      icon: <DatabaseOutlined />,
    },
    {
      path: "inventory/stock-adjustment",
      breadcrumbName: t("stock_adjustment"),
      icon: <EditOutlined />,
    },
  ];

  const tableColumns = [
    {
      title: t("date"),
      dataIndex: "date_time",
      render: (date_time) => {
        return date_time
          ? moment(date_time).format("MMM. D, YYYY hh:mm a")
          : "-";
      },
    },
    {
      title: t("reference_no"),
      dataIndex: "reference_no",
    },
    {
      title: t("note"),
      dataIndex: "note",
      render: (note) => {
        return (
          <>
            {note && (
              <Popover
                content={
                  <div style={{ maxWidth: "250px", textAlign: "center" }}>
                    {note}
                  </div>
                }
                trigger="click"
              >
                <ProfileOutlined
                  style={{
                    fontSize: "20px",
                    color: "var(--color-primary-hover)",
                  }}
                />
              </Popover>
            )}
          </>
        );
      },
    },
    {
      title: t("branch"),
      dataIndex: "branch",
      render: (branch) => {
        return branch.name;
      },
    },
    {
      title: t("created_by"),
      render: (record) => {
        return <>{record.created_by.name}</>;
      },
    },
    {
      title: t("quantity"),
      align: "center",
      dataIndex: "total_quantity",
      render: (total_quantity) => {
        return (
          <>
            <div
              style={{
                color:
                  Math.sign(total_quantity) === 1
                    ? "var(--color-success)"
                    : "var(--color-danger)",
              }}
            >
              {total_quantity}
            </div>
          </>
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              style={{ padding: 2 }}
              onClick={() => {
                navigate("/inventory/stock-adjustment/" + record.id);
              }}
            >
              <EyeOutlined title={t("view_details")} />
            </Button>
          </Space>
        );
      },
    },
  ];

  const productTableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("image"),
      width: "85px",
      render: (record) => {
        return (
          <>
            <img
              alt={record.product.name_en}
              src={
                record.product.image
                  ? record.product.image
                  : "/images/image-not-found.png"
              }
              className="app-table-thumbnail"
            />
          </>
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          <>
            {appConfig.direction === "ltr"
              ? record.product.name_en
              : record.product.name}
          </>
        );
      },
    },
    {
      title: t("current_stock"),
      align: "center",
      dataIndex: "quantity",
    },
    {
      title: t("quantity"),

      render: (record) => {
        return (
          <>
            <QuantityStepper
              value={record.qty}
              min={1}
              max={
                record.adjustment_type === "subtraction"
                  ? getAvailableStock(record.quantity)
                  : undefined
              }
              showInput
              decreaseDisabled={record.qty <= 1}
              increaseDisabled={
                record.adjustment_type === "subtraction" &&
                record.qty >= getAvailableStock(record.quantity)
              }
              onDecrease={() => adjustmentReducer(record, "dec")}
              onIncrease={() => adjustmentReducer(record, "inc")}
              onChange={(quantity) =>
                adjustmentReducer(record, "directInput", quantity)
              }
            />
          </>
        );
      },
    },
    {
      title: t("type"),

      render: (record) => {
        return (
          <>
            <Select
              value={record.adjustment_type}
              style={{
                width: 120,
              }}
              onChange={(e) => adjustmentReducer(record, e)}
              options={[
                {
                  value: "addition",
                  label: t("addition"),
                },
                {
                  value: "subtraction",
                  label: t("subtraction"),
                  disabled: getAvailableStock(record.quantity) < 1,
                },
              ]}
            />
          </>
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: "85px",
      render: (record) => {
        return (
          <Space>
            <Popconfirm
              title={t("warning_remove")}
              onConfirm={() => adjustmentReducer(record, "remove")}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const handleProductSelect = (value) => {
    const product = productsList.find(
      (product) => product.product.id === value,
    );
    product.product.product_id = product.product.id;
    adjustmentReducer(product, "add");
    adjustmentForm.setFieldsValue({
      product: "",
    });
  };

  const adjustmentReducer = (product, action, quantity) => {
    let updatedAdjustment = JSON.parse(JSON.stringify(adjustmentData));
    let productId = product.product.product_id;
    let indexFound = updatedAdjustment.findIndex(
      (product) => product.product.product_id === productId,
    );
    if (action === "add") {
      if (indexFound === -1) {
        product.qty = 1;
        product.adjustment_type = "addition";
        updatedAdjustment.push(product);
      } else {
        showAppError(t("product_already_selected"));
      }
    } else if (action === "inc") {
      const item = updatedAdjustment[indexFound];
      item.qty =
        item.adjustment_type === "subtraction"
          ? clampQuantityToAvailableStock(item.qty + 1, item.quantity)
          : normalizePositiveQuantity(item.qty + 1);
    } else if (action === "dec") {
      updatedAdjustment[indexFound].qty = normalizePositiveQuantity(
        updatedAdjustment[indexFound].qty - 1,
      );
    } else if (action === "remove") {
      updatedAdjustment.splice(indexFound, 1);
    } else if (action === "addition" || action === "subtraction") {
      const item = updatedAdjustment[indexFound];

      if (action === "subtraction" && getAvailableStock(item.quantity) < 1) {
        showAppError(t("insufficient_quantity"));
        return;
      }

      item.adjustment_type = action;
      item.qty =
        action === "subtraction"
          ? clampQuantityToAvailableStock(item.qty, item.quantity)
          : normalizePositiveQuantity(item.qty);
    } else if (action === "directInput") {
      const item = updatedAdjustment[indexFound];
      item.qty =
        item.adjustment_type === "subtraction"
          ? clampQuantityToAvailableStock(quantity, item.quantity)
          : normalizePositiveQuantity(quantity);
    }
    setAdjustmentData(updatedAdjustment);
  };

  const handleAddModal = () => {
    setModalVisible(true);
    setAdjustmentData([]);
    setProductsList([]);
    setBranchId(authUser.type === UserType.Employee ? authUser.branch_id : 0);
  };

  const handleCancel = () => {
    // close modal
    setModalVisible(false);
    // Reset Form Fields
    adjustmentForm.resetFields();
    setAdjustmentData([]);
    setBranchId(0);
  };

  const handleAddEdit = () => {
    adjustmentForm
      .validateFields()
      .then((values) => {
        const hasInvalidSubtraction = adjustmentData.some(
          (item) =>
            item.adjustment_type === "subtraction" &&
            !isQuantityWithinAvailableStock(item.qty, item.quantity),
        );

        if (hasInvalidSubtraction) {
          showAppError(t("insufficient_quantity"));
          return;
        }

        setLoadingBTN(true);
        // Sending Request to API
        let transactionObj = {
          branch_id: values.branch_id,
          note: values.note === undefined ? "" : values.note,
        };
        transactionObj.stocks = adjustmentData.map((item) => ({
          product_id: item.product.product_id
            ? item.product.product_id
            : item.product.id,
          quantity:
            item.adjustment_type === "subtraction" ? -item.qty : item.qty,
        }));

        createStockAdjustments(transactionObj)
          .then((response) => {
            let data = response.data || response.response.data;
            if (data.success === true) {
              setReloadTableData(!reloadTableData);
              // Reset Form Fields
              adjustmentForm.resetFields();
              // Hide Modal
              setModalVisible(false);
              // Success
              NotificationWithIcon(
                "success",
                t("stock_adjustment_done"),
                t("stock_adjustment_done_successfully"),
              );
            } else {
              handleApiError(response, t);
            }
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            // Set Loading False
            setLoadingBTN(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleTableChange = (pagination) => {
    setPaginationData(pagination);
    setCurrentPage(pagination.current);
    setPerPageSize(pagination.pageSize);
  };

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("stock_adjustment")}
          ghost={false}
          footer={authUser.type !== UserType.Employee && searchForm}
          extra={[
            (authUser.can_request_stock_adjustment ||
              authUser.type === UserType.BusinessOwner) && (
              <Button type="primary" onClick={handleAddModal} key={1}>
                + {t("adjustment_add")}
              </Button>
            ),
          ]}
        />
      </Card>
      <Card>
        <h3>{t("adjustment_history")}</h3>
        <Table
          columns={tableColumns}
          dataSource={stockAdjustmentData}
          loading={isDataLoading}
          rowKey="id"
          pagination={{
            current: paginationData.current_page,
            pageSize: paginationData.per_page,
            total: paginationData.total,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </Card>
      <Modal
        open={modalVisible}
        title={t("adjustment_add")}
        onOk={handleAddEdit}
        onCancel={handleCancel}
        width={1000}
        footer={[
          <Button
            type="default"
            key="back"
            loading={loadingBTN}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingBTN}
            onClick={handleAddEdit}
          >
            {t("add")}
          </Button>,
        ]}
      >
        <Form
          name="add-Adjustment-form"
          layout="vertical"
          onFinish={handleAddEdit}
          className="app-form-full"
          form={adjustmentForm}
          initialValues={{
            branch_id:
              authUser.type === UserType.Employee
                ? authUser.branch_id
                : undefined,
          }}
        >
          <Row gutter={[20, 20]}>
            <Col
              className="gutter-row"
              xs={24}
              sm={12}
              md={12}
              lg={12}
              xl={12}
              xxl={12}
            >
              <Form.Item
                name="branch_id"
                label={t("branch")}
                rules={[
                  {
                    required: true,
                    message: t("validate_branch_required"),
                  },
                ]}
                hasFeedback
              >
                <Select
                  disabled={authUser.type === UserType.Employee}
                  showSearch
                  allowClear
                  onChange={(e) => {
                    setBranchId(e || 0);
                    setAdjustmentData([]);
                    setProductsList([]);
                  }}
                  options={branchesData.map((branch) => {
                    return {
                      label: branch.name,
                      value: branch.id,
                    };
                  })}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>

            {branchId ? (
              <Col
                className="gutter-row"
                xs={24}
                sm={12}
                md={12}
                lg={12}
                xl={12}
                xxl={12}
              >
                <Form.Item
                  name="product"
                  label={t("product_add")}
                  rules={[
                    adjustmentData.length < 1 && {
                      required: true,
                      message: t("validate_product_name_required"),
                    },
                  ]}
                >
                  <Select
                    showSearch
                    options={productsList.map((product) => {
                      return {
                        label:
                          appConfig.direction === "ltr"
                            ? `${product.product.name_en} (${product.quantity})`
                            : `${product.product.name} (${product.quantity})`,
                        value: product.product.id,
                      };
                    })}
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    onChange={handleProductSelect}
                  />
                </Form.Item>
              </Col>
            ) : (
              ""
            )}
            {adjustmentData.length > 0 && (
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={24}
              >
                <Table
                  columns={productTableColumns}
                  dataSource={adjustmentData}
                  rowKey="id"
                  pagination={false}
                />
              </Col>
            )}
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={24}
              xxl={24}
            >
              <Form.Item name="note" label={t("note")}>
                <TextArea rows={4} />
              </Form.Item>
            </Col>
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={24}
              lg={24}
              xl={24}
              xxl={24}
            >
              <Form.Item name="id" className="app-hidden">
                <input type="hidden" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default StockAdjustment;
